import type { TypeIconClasses } from '.';
import type { ReadChangelogType } from '@readme/api/src/mappings/changelog/types';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import useClassy from '@core/hooks/useClassy';
import useRdmdOpts from '@core/hooks/useRdmdOpts';
import { useProjectStore, useSuperHubStore } from '@core/store';

import DateLine, { LocalizedDateLine } from '@ui/DateLine';
import RDMD from '@ui/RDMD';

import classes from './style.module.scss';

interface ChangelogExcerptProps {
  changelog: ReadChangelogType['data'];
  exactDate: boolean;
  typeIconClasses: TypeIconClasses;
}

export default function ChangelogExcerpt({ changelog, exactDate, typeIconClasses }: ChangelogExcerptProps) {
  const { t } = useTranslation();
  const bem = useClassy(classes, 'ChangelogPost');
  const showAuthor = useProjectStore(s => !!s.data.appearance.changelog.show_author);
  const isi18nEnabled = useSuperHubStore(s => s.isI18nEnabled);
  const rdmdOpts = useRdmdOpts();

  return (
    <article className={bem('&', 'ChangelogPostExcerpt')}>
      {!!changelog.type && changelog.type !== 'none' && (
        <div className={bem('_type')}>
          {!!typeIconClasses[changelog.type] && (
            <span className={`${typeIconClasses[changelog.type]} ${classes.ChangelogIcon}`} />
          )}
          {t(`changelog.${changelog.type}`)}
        </div>
      )}
      <h2 className={bem('_title', '_titleexcerpt')}>
        <Link
          to={{
            pathname: `/changelog/${changelog.slug}`,
            state: { fromChangelogList: true },
          }}
        >
          {changelog.title}
        </Link>
      </h2>
      {isi18nEnabled ? (
        <LocalizedDateLine
          attribution={showAuthor && changelog.author.name ? changelog.author.name : undefined}
          className={bem('_date', '_noicon')}
          exactDate={exactDate}
          icon={exactDate ? undefined : 'clock'}
          time={changelog.created_at}
        />
      ) : (
        <DateLine
          className={bem('_date', '_noicon')}
          icon={exactDate ? '' : 'icon-clock'}
          suffix={showAuthor && changelog.author.name ? `by ${changelog.author.name}` : undefined}
          time={changelog.created_at}
          {...(exactDate ? { fnsFormat: 'PPP' } : {})}
        />
      )}
      <RDMD
        body={changelog?.content.body}
        className={bem('_text')}
        excerpt
        format="mdx"
        opts={rdmdOpts}
        Tag="section"
      />
    </article>
  );
}
